import { getSite, getIsPreview } from './siteUtils';
import { getStage } from './roadieHelper';
import { getItemImageUrl, getAllProductImageUrls, getItemImageWithFallback, getAllProductImagesWithFallback, ImageSize } from '@amzn/d2c-application-website-common';
import type { ResolvedImage } from '@amzn/d2c-application-website-common';

export { ImageSize, getItemImageUrl, getAllProductImageUrls, getItemImageWithFallback, getAllProductImagesWithFallback } from '@amzn/d2c-application-website-common';
export type { ResolvedImage } from '@amzn/d2c-application-website-common';

/**
 * Legacy sites set up under the old Mulberry convention use a short name
 * as the MediaCentral folder instead of the full hostname.
 */
const MEDIA_CENTRAL_SITE_MAP: Record<string, string> = {
  'shop.plumeria.com': 'pesopluma',
  'store.pesopluma.com': 'pesopluma',
  'merch.zinnia.local': 'dojacat',
  'merch.dojacat.com': 'dojacat',
  'merch.strelitzia.com': 'lesserafim',
  'merch.le-sserafim.us': 'lesserafim',
  'merch.derby.com': 'jackharlow',
  'merch.jackharlow.com': 'jackharlow',
  'shop.tormentedvalley.com': 'thetormentedvalley',
  'shop.thetormentedvalley.com': 'thetormentedvalley',
  'merch.mulberry-test.com': 'mulberrytest',
};

/**
 * Resolves the MediaCentral folder name for the current site.
 * Legacy sites map to a short name; new sites use the full hostname.
 */
export const getMediaCentralSiteId = (): string | undefined => {
  let siteId: string;
  try {
    const stage = getStage();
    const env = getIsPreview() ? 'PREVIEW' : 'LIVE';
    siteId = getSite(stage, env);
  } catch {
    siteId = window.location.hostname;
  }
  if (!siteId) return undefined;
  return MEDIA_CENTRAL_SITE_MAP[siteId] ?? siteId;
};

/**
 * Resolves any image to both its primary URL and fallback retail URL.
 * If useTransparentImages is true, primary is MediaCentral transparent PNG with retail as fallback.
 * Otherwise both src and fallback are the same retail URL.
 *
 * @param img - Image entry (string URL, or {highRes: {url}, lowRes: {url}} object)
 * @param useTransparentImages - Whether to convert to MediaCentral
 * @param size - Image size (default: SMALL)
 */
export const resolveImageUrl = (img: any, useTransparentImages: boolean, size?: ImageSize): ResolvedImage => {
  const siteName = useTransparentImages ? getMediaCentralSiteId() : undefined;
  return getItemImageWithFallback(img, siteName, size);
};

/**
 * Resolves all images from a catalog item's images array.
 * Returns both MediaCentral URLs and retail fallbacks.
 *
 * @param images - Product images array from catalog
 * @param useTransparentImages - Whether to use MediaCentral transparent PNGs
 * @param size - Image size (default: LARGE for product detail)
 */
export const resolveAllImageUrls = (images: any[] | undefined, useTransparentImages: boolean, size?: ImageSize): ResolvedImage[] => {
  const siteName = useTransparentImages ? getMediaCentralSiteId() : undefined;
  return getAllProductImagesWithFallback(images, siteName, size);
};
